import { useEffect, useRef, useState } from "react";

/**
 * useVoiceRecorder — Lightweight MediaRecorder wrapper that:
 *  - Picks the best MIME type each browser supports (webm/opus on Chrome/Android,
 *    mp4/aac on Safari/iOS).
 *  - Keeps a running clock so the UI can show elapsed seconds.
 *  - Returns a single Blob + MIME at stop() time — audio bytes stay in RAM and
 *    are released to GC as soon as the caller uploads them (no local persistence).
 *
 *  Usage:
 *    const rec = useVoiceRecorder({ maxSeconds: 180 });
 *    await rec.start();
 *    ...
 *    const { blob, mimeType } = await rec.stop();
 */
const PREFERRED_MIMES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
  "audio/ogg;codecs=opus",
];

function pickSupportedMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const mt of PREFERRED_MIMES) {
    try {
      if (MediaRecorder.isTypeSupported(mt)) return mt;
    } catch (_) {}
  }
  return "";
}

export default function useVoiceRecorder({ maxSeconds = 180 } = {}) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const stopResolverRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    const ok =
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof MediaRecorder !== "undefined";
    setSupported(!!ok);
  }, []);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
    setRecording(false);
  };

  const start = async () => {
    setError(null);
    if (!supported) {
      setError("Microphone recording isn't supported in this browser.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickSupportedMime();
      const options = mime ? { mimeType: mime } : undefined;
      const rec = new MediaRecorder(stream, options);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        const parts = chunksRef.current;
        const mt = rec.mimeType || mime || "audio/webm";
        const blob = parts.length ? new Blob(parts, { type: mt }) : null;
        if (stopResolverRef.current) {
          stopResolverRef.current({ blob, mimeType: mt });
          stopResolverRef.current = null;
        }
        cleanup();
      };
      rec.start(250); // emit a chunk every 250ms so stop() is snappy
      setRecording(true);
      const started = Date.now();
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - started) / 1000);
        setElapsed(secs);
        if (secs >= maxSeconds) stop();
      }, 250);
      return true;
    } catch (err) {
      setError(
        err?.name === "NotAllowedError"
          ? "Microphone permission denied. Allow mic access to dictate."
          : err?.message || "Could not start the microphone."
      );
      cleanup();
      return false;
    }
  };

  const stop = () =>
    new Promise((resolve) => {
      const rec = mediaRecorderRef.current;
      if (!rec || rec.state === "inactive") {
        resolve({ blob: null, mimeType: null });
        cleanup();
        return;
      }
      stopResolverRef.current = resolve;
      try {
        rec.stop();
      } catch (_) {
        resolve({ blob: null, mimeType: null });
        cleanup();
      }
    });

  const cancel = () => {
    const rec = mediaRecorderRef.current;
    stopResolverRef.current = null;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch (_) {}
    }
    cleanup();
  };

  useEffect(() => () => cancel(), []); // cleanup on unmount

  return { start, stop, cancel, recording, elapsed, error, supported };
}
